"use client";

import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import React, { useState, useEffect } from "react";
import { redirect } from "next/navigation";
import { Icons } from "./icons";
import { ConfettiExploder } from '@/components/confetti-exploder';
import { toast } from "@/components/ui/use-toast"
import { usePostHog } from "posthog-js/react"

interface CheckoutCompleteDialogProps {
    userId: string;
    hexColour: string;
    sessionId: string;
    name: string | null;
    slug: string;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
}

export function CheckoutCompleteDialog({ userId, name, slug, hexColour, sessionId, isOpen, onOpenChange}: CheckoutCompleteDialogProps) {
    const [checkoutSessionStatus, setCheckoutSessionStatus] = useState<string | null>(null);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const posthog = usePostHog();

    async function getCheckoutSession(sessionId: string, userId: string) {
        try {
          const response = await fetch(`/api/connect/get-checkout-session?checkoutSessionId=${sessionId}&userId=${userId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
      
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
      
          const data = await response.json();
          return data;
        } catch (error) {
          console.error('Error fetching checkout session:', error);
          throw error;
        }
    }

    useEffect(() => {
        posthog.capture(
            'Customer completed checkout',
            {
                provider_user_id: userId,
                provider_username: slug,
                provider_name: name,
            }
        )
    }, [])

    useEffect(() => {
        if (sessionId && userId) {
            getCheckoutSession(sessionId, userId)
                .then(session => {
                    setCheckoutSessionStatus(session.status);
                    setUserEmail(session.userEmail);
                })
                .catch(error => {
                    return toast({
                        title: 'Something went wrong, sorry! Please try again.',
                        variant: "destructive",
                    })
                });
        }
    }, [sessionId, userId]); // Only re-run the effect if sessionId or userId changes

    if (checkoutSessionStatus === 'open') {
        return (
            redirect(`/${slug}`)
        )
    }

    if (checkoutSessionStatus === 'complete') {
        return (
            <div className="fixed top-4 right-4 z-50">
              <Dialog open={isOpen} onOpenChange={onOpenChange}>
                <ConfettiExploder />
                <DialogContent className="py-10 rounded-2xl">
                    <DialogHeader className="flex items-center justify-center space-y-2 mx-4 mt-4">
                    <div style={{borderColor: hexColour}} className="flex items-center justify-center bg-white rounded-full border-2 border-white shadow-xl overflow-hidden w-16 h-16">
                        <Icons.check style={{color: hexColour}} width={40} height={40} />
                    </div>
                    </DialogHeader>
                    <div className="flex flex-col items-center text-center space-y-2 m-4">
                        <h1 className="text-2xl text-zinc-800 font-bold">Thank you! &#x2764;&#xfe0f;</h1>
                        <h2 className="text-xl text-zinc-800 font-semibold">You will receive a confirmation email shortly.</h2>
                        {userEmail ? (
                          <p className="text-md text-zinc-800 italic">If you have any questions, please email me at <a className="underline" href={`mailto:${userEmail}`}>{userEmail}</a></p>
                        ) : (
                          <p className="text-md text-zinc-800 italic">If you have any questions, please send me a message on my socials</p>
                        )}
                    </div>
                </DialogContent>
              </Dialog>
            </div>
      )
    }

    return null;
}